import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  currentWorkspaceKeys,
  toApiError,
  useApi,
  type ApiError,
  type UpdateWorkspaceSettingsRequest,
} from '@/shared/api';

/**
 * Speichert die Stammdaten. Danach gilt der gespeicherte Stand sofort — auch als Vorbelegung im
 * Anlage-Assistenten — und die Stripe-Seite lädt neu, weil eine neue Währung die Verbindung
 * wieder auf „Prüfung offen“ setzt.
 */
export function useWorkspaceSettingsSave() {
  const api = useApi();
  const queryClient = useQueryClient();
  const [problem, setProblem] = useState<ApiError | null>(null);

  const mutation = useMutation({
    mutationFn: (body: UpdateWorkspaceSettingsRequest) => api.workspace.updateSettings(body),
    // Offline pausiert TanStack sonst still, und „Speichern“ drehte sich ohne Ende.
    networkMode: 'always',
    onMutate: () => setProblem(null),
    onSuccess: async (saved) => {
      queryClient.setQueryData(currentWorkspaceKeys.settings, saved);
      await queryClient.invalidateQueries({
        queryKey: currentWorkspaceKeys.workspace,
        exact: true,
      });
      await queryClient.invalidateQueries({ queryKey: ['stripeSettings'] });
      toast.success('Stammdaten gespeichert');
    },
    onError: async (failure) => {
      const next = await toApiError(failure);
      setProblem(next);
      // Ohne Antwort ist offen, ob gespeichert wurde: der Serverstand wird neu geladen. Ist er
      // unverändert, bleiben die Eingaben stehen; hat der Server gespeichert, zeigt das Formular ihn.
      if (next.status === 0) {
        await queryClient.invalidateQueries({ queryKey: currentWorkspaceKeys.settings });
      }
    },
  });

  return { save: mutation.mutate, saving: mutation.isPending, problem };
}
